import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormModel } from '@types'

import { Option, Select } from '@components/Select'

import { useAvailableFormModels } from '@hooks/form-model/available-form-models.hook'

import { CheckedCircle, ItemContainer, SelectedItemLabel, WrappedComponentContainer } from './SelectFormModel.styled'

interface Props {
  required?: boolean
  projectId: string
  formModel?: FormModel
  onSelectFormModel: (formModel?: FormModel) => void
  WrappedComponent?: () => JSX.Element
  disabledSearch?: boolean
}

export const SelectFormModel: React.FC<Props> = (props: Props): JSX.Element => {
  const { required, projectId, formModel, onSelectFormModel, disabledSearch = false, WrappedComponent } = props

  const { t } = useTranslation()

  const [search, setSearch] = useState<string>(formModel?.title || '')

  useEffect(() => {
    setSearch(formModel?.title || '')
  }, [formModel, WrappedComponent])

  const { data } = useAvailableFormModels(projectId)

  const selectedOption: Option | undefined = formModel && {
    id: formModel.id,
    label: formModel.title,
    data: formModel,
    ...(disabledSearch
      ? {
          Component: () => (
            <ItemContainer>
              <SelectedItemLabel>{formModel.title}</SelectedItemLabel>
            </ItemContainer>
          ),
        }
      : {}),
  }

  const options: Option[] = (data?.availableFormModels || []).map((formModel) => ({
    id: formModel.id,
    label: formModel.title,
    data: formModel,
  }))

  const filteredOptions = WrappedComponent
    ? options
    : options.filter((option) => option.label.toLowerCase().indexOf((search || '').toLowerCase()) != -1)

  return (
    <>
      <Select
        WrappedComponent={
          WrappedComponent
            ? ({ show }) => (
                <WrappedComponentContainer onClick={show}>
                  <WrappedComponent />
                  {selectedOption && <CheckedCircle />}
                </WrappedComponentContainer>
              )
            : undefined
        }
        disabledVerticalCorrection
        required={required}
        label={t('modules_shared_components_SelectFormModel_label')}
        placeholder={t('modules_shared_components_SelectFormModel_placeholder')}
        emptyLabel={t('modules_shared_components_SelectFormModel_empty')}
        search={disabledSearch ? undefined : search}
        onSearch={disabledSearch ? undefined : setSearch}
        selected={selectedOption}
        options={disabledSearch ? options : filteredOptions}
        onClick={(option) => {
          if (option) {
            setSearch(option.label)

            onSelectFormModel(option.data as FormModel)
          } else {
            setSearch('')

            onSelectFormModel()
          }
        }}
      />
    </>
  )
}
